// Resolves the backoffice "Block order" (apartment_fields_order.block) entries
// into displayable { title, value } rows for the hover popup / mobile strip.
//
// The backend only puts a subset of fields into `apartment.display_fields`
// (e.g. `product_acf_floor_name` but NOT `product_acf_floor` or
// `product_floor_entity_number`), so a lookup in display_fields alone leaves
// configured fields blank. We fall back to the apartment's own properties,
// mirroring olyo-ai's `useApartmentFields.resolveValue`.

const isEmpty = (v) => v === undefined || v === null || v === "";

const firstFilled = (...values) => values.find((v) => !isEmpty(v));

const fallbackValue = (apartment, field) => {
  switch (field) {
    case "product_acf_floor":
    case "product_floor":
    case "product_floor_entity_number":
      return firstFilled(
        apartment.floor_number,
        apartment.floor,
        apartment.floor_name
      );
    case "product_acf_floor_name":
    case "product_floor_entity_name":
      return firstFilled(apartment.floor_name, apartment.floor_number);
    case "product_post_title":
      return apartment.name;
    case "product_id":
      return apartment.id;
    case "product_price":
      return firstFilled(apartment.price, apartment.data?.price);
    case "product_acf_available":
      return firstFilled(apartment.available, apartment.data?.available);
    case "apartment_direction":
      return firstFilled(apartment.data?.direction, apartment.direction);
    case "apartment_type_post_title":
      return apartment.at?.name;
    case "apartment_type_rooms":
      return firstFilled(apartment.type_rooms, apartment.at?.rooms);
    case "building_post_title":
      return apartment.type_building?.name;
    default:
      return undefined;
  }
};

export const resolveFieldValue = (apartment, field) => {
  if (!apartment || !field) return undefined;
  const direct = apartment.display_fields?.find((f) => f.field === field);
  if (direct && !isEmpty(direct.value)) return direct.value;
  return fallbackValue(apartment, field);
};

/**
 * Rows in exactly the order configured in the backoffice. Empty values are
 * skipped, and an entry that renders identically (same title + value) to an
 * earlier one is dropped so a field configured twice shows once.
 */
export const resolveBlockRows = (apartment, blockFieldsOrder) => {
  if (!apartment || !Array.isArray(blockFieldsOrder)) return [];
  const seen = new Set();
  const rows = [];
  blockFieldsOrder.forEach((detail, index) => {
    const value = resolveFieldValue(apartment, detail?.field);
    if (isEmpty(value)) return;
    const title = detail.title || "";
    const key = `${title}|${value}`;
    if (seen.has(key)) return;
    seen.add(key);
    rows.push({ key: `${detail.field}-${index}`, title, value });
  });
  return rows;
};
